import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { Alert, Button, Dialog, Field, IconButton, Input, Select } from "../../ui";
import "./styles.scss";

type TournamentFormat = "League" | "Group" | "Knockout";

interface FastTournamentData {
  teams: string[];
  format: TournamentFormat;
  generatedData: any;
  createdAt: string;
}

interface FastTournamentDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const FastTournamentDialog = (props: FastTournamentDialogProps) => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState<string[]>([""]);
  const [format, setFormat] = useState<TournamentFormat>("League");
  const [numberOfGroups, setNumberOfGroups] = useState<number>(2);
  const [errorMessage, setErrorMessage] = useState("");

  // Calculate valid number of groups based on number of teams
  const getValidNumberOfGroups = () => {
    const validTeams = teams.filter((team) => team.trim() !== "");
    if (validTeams.length < 2) return [2];
    const maxGroups = validTeams.length;
    return Array.from({ length: maxGroups - 1 }, (_, i) => i + 2);
  };

  useEffect(() => {
    if (props.isOpen) {
      setTeams([""]);
      setFormat("League");
      setNumberOfGroups(2);
      setErrorMessage("");
    }
  }, [props.isOpen]);

  // Update number of groups when teams change to ensure it's valid
  useEffect(() => {
    if (format === "Group") {
      const validGroupOptions = getValidNumberOfGroups();
      if (validGroupOptions.length > 0 && !validGroupOptions.includes(numberOfGroups)) {
        // If current number of groups is invalid, set to the first valid option
        setNumberOfGroups(validGroupOptions[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, format]);

  const handleAddTeam = () => {
    setTeams([...teams, ""]);
  };

  const handleRemoveTeam = (index: number) => {
    if (teams.length > 1) {
      setTeams(teams.filter((_, i) => i !== index));
    }
  };

  const handleTeamChange = (index: number, value: string) => {
    const updatedTeams = [...teams];
    updatedTeams[index] = value;
    setTeams(updatedTeams);
  };

  const generateLeagueMatches = (teamList: string[]) => {
    const matches: any[] = [];
    for (let i = 0; i < teamList.length; i++) {
      for (let j = i + 1; j < teamList.length; j++) {
        matches.push({
          homeTeam: teamList[i],
          awayTeam: teamList[j],
          round: "Round 1",
        });
      }
    }
    return matches;
  };

  const generateGroupMatches = (teamList: string[], numGroups: number) => {
    const groups: { [key: string]: string[] } = {};
    const shuffled = [...teamList].sort(() => Math.random() - 0.5);

    for (let i = 0; i < numGroups; i++) {
      const groupName = String.fromCharCode(65 + i); // A, B, C, etc.
      groups[groupName] = [];
    }

    shuffled.forEach((team, index) => {
      const groupName = String.fromCharCode(65 + (index % numGroups));
      groups[groupName].push(team);
    });

    const matches: any[] = [];
    Object.entries(groups).forEach(([groupName, groupTeams]) => {
      for (let i = 0; i < groupTeams.length; i++) {
        for (let j = i + 1; j < groupTeams.length; j++) {
          matches.push({
            homeTeam: groupTeams[i],
            awayTeam: groupTeams[j],
            group: groupName,
            round: `Group ${groupName}`,
          });
        }
      }
    });

    return { groups, matches };
  };

  const generateKnockoutMatches = (teamList: string[]) => {
    const matches: any[] = [];
    const shuffled = [...teamList].sort(() => Math.random() - 0.5);

    // Calculate number of rounds needed
    const numTeams = shuffled.length;
    const numRounds = Math.ceil(Math.log2(numTeams));

    // Generate first round matches
    let roundNumber = 1;
    let currentRoundTeams = [...shuffled];

    // Generate all rounds
    while (roundNumber <= numRounds && currentRoundTeams.length > 1) {
      const roundMatches: any[] = [];
      const nextRoundTeams: string[] = [];

      for (let i = 0; i < currentRoundTeams.length; i += 2) {
        if (i + 1 < currentRoundTeams.length) {
          // Pair two teams
          roundMatches.push({
            homeTeam: currentRoundTeams[i],
            awayTeam: currentRoundTeams[i + 1],
            round: `Round ${roundNumber}`,
          });
          // Winner placeholder for next round
          nextRoundTeams.push(`Winner of Match ${roundMatches.length}`);
        } else {
          // Bye - team advances automatically
          nextRoundTeams.push(currentRoundTeams[i]);
        }
      }

      matches.push(...roundMatches);
      currentRoundTeams = nextRoundTeams;
      roundNumber++;
    }

    return matches;
  };

  const generateCustomMatches = (teamList: string[]) => {
    // Custom format: combination of league and knockout
    const midPoint = Math.floor(teamList.length / 2);
    const leagueTeams = teamList.slice(0, midPoint);
    const knockoutTeams = teamList.slice(midPoint);

    const leagueMatches = generateLeagueMatches(leagueTeams);
    const knockoutMatches = generateKnockoutMatches(knockoutTeams);

    return {
      league: {
        teams: leagueTeams,
        matches: leagueMatches,
      },
      knockout: {
        teams: knockoutTeams,
        matches: knockoutMatches,
      },
    };
  };

  const handleGenerate = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    const validTeams = teams.filter((team) => team.trim() !== "");

    if (validTeams.length < 2) {
      setErrorMessage("Please enter at least 2 teams");
      return;
    }

    if (format === "Knockout" && validTeams.length < 2) {
      setErrorMessage("Knockout format requires at least 2 teams");
      return;
    }

    if (format === "Group") {
      if (numberOfGroups < 2) {
        setErrorMessage("Please select at least 2 groups");
        return;
      }
      if (numberOfGroups > validTeams.length) {
        setErrorMessage("Number of groups cannot exceed number of teams");
        return;
      }
    }

    setErrorMessage("");

    let generatedData: any;

    switch (format) {
      case "League":
        generatedData = {
          format: "League",
          teams: validTeams,
          matches: generateLeagueMatches(validTeams),
        };
        break;
      case "Group":
        const groupData = generateGroupMatches(validTeams, numberOfGroups);
        generatedData = {
          format: "Group",
          teams: validTeams,
          numberOfGroups,
          ...groupData,
        };
        break;
      case "Knockout":
        generatedData = {
          format: "Knockout",
          teams: validTeams,
          matches: generateKnockoutMatches(validTeams),
        };
        break;
      default:
        setErrorMessage("Invalid tournament format");
        return;
    }

    const fastTournamentData: FastTournamentData = {
      teams: validTeams,
      format,
      generatedData,
      createdAt: new Date().toISOString(),
    };

    // Store in sessionStorage
    sessionStorage.setItem("fastTournament", JSON.stringify(fastTournamentData));

    props.onClose();

    // Navigate to fast tournament page
    navigate("/dashboard/fast-tournament");
  };

  const handleClose = () => {
    setTeams([""]);
    setFormat("League");
    setNumberOfGroups(2);
    setErrorMessage("");
    props.onClose();
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={handleClose}
      title="Quick Tournament"
      bodyClassName="fast-tournament-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleGenerate}>Generate Tournament</Button>
        </>
      }
    >
      {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

      <Field label="Tournament Format" required>
        {(id) => (
          <Select id={id} block value={format} onChange={(e) => setFormat(e.target.value as TournamentFormat)}>
            <option value="League">League</option>
            <option value="Group">Group</option>
            <option value="Knockout">Knockout</option>
          </Select>
        )}
      </Field>

      {format === "Group" && (
        <Field label="Number of Groups" required>
          {(id) => (
            <Select id={id} block value={numberOfGroups} onChange={(e) => setNumberOfGroups(parseInt(e.target.value))}>
              {getValidNumberOfGroups().map((num) => (
                <option key={num} value={num}>
                  {num} {num === 1 ? "Group" : "Groups"}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}

      <Field label="Team Names" required>
        {(id) => (
          <div className="teams-field">
            <div className="teams-list">
              {teams.map((team, index) => (
                <div key={index} className="team-input-row">
                  <Input
                    id={index === 0 ? id : undefined}
                    type="text"
                    value={team}
                    onChange={(e) => handleTeamChange(index, e.target.value)}
                    placeholder={`Team ${index + 1}`}
                  />
                  {teams.length > 1 && (
                    <IconButton
                      variant="danger"
                      label="Remove team"
                      icon={<Trash2 />}
                      onClick={() => handleRemoveTeam(index)}
                    />
                  )}
                </div>
              ))}
            </div>
            <Button variant="tint" size="sm" icon={<Plus />} onClick={handleAddTeam} className="add-team-btn">
              Add Team
            </Button>
          </div>
        )}
      </Field>
    </Dialog>
  );
};

export default FastTournamentDialog;
